import { io, Socket } from "socket.io-client";
import { t } from "@/i18n";
import { getConfigValue } from "@/config-loader";

interface AuthResponse {
  message: string;
  token: string;
  user: {
    id: number;
    username: string;
    email: string;
    role: string;
  };
}

type RealtimeCallback = (
  tag: string,
  value: any,
  doctype: string | null,
) => void;

type Listener = () => void;

/**
 * Hook to bridge incoming WebSocket DFT messages with the machines store/cache.
 * Phase 3.7 (TanStack Query hooks) will wire this; until then a no-op keeps
 * the socket pipeline working and views consume updates via window CustomEvents.
 */
type MachineStatusUpdater = (serial: string, statusValue: string) => void;

let machineStatusUpdater: MachineStatusUpdater = () => {};

export function setMachineStatusUpdater(fn: MachineStatusUpdater) {
  machineStatusUpdater = fn;
}

function ensureStringValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return null;
}

function ensureNumericValue(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const parsed = parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

class SocketService {
  socket: Socket | null = null;
  isConnected = false;
  isAuthenticated = false;
  authToken: string | null = null;
  error: string | null = null;

  private serverUrl = "";
  private defaultTopic = "";
  private httpBase = "";
  subscribedTopics: string[] = [];
  private realtimeCallbacks = new Map<string, RealtimeCallback>();
  private listeners = new Set<Listener>();
  private isInitializing = false;
  // Serials de máquinas cujos tópicos por device (`<prefix>-maxiot-<serial>`) devem
  // ser assinados. Mantido para re-assinar automaticamente em cada reconexão.
  private machineSerials = new Set<string>();

  private notify() {
    this.listeners.forEach((fn) => {
      try {
        fn();
      } catch (e) {
        console.warn("[socketService] listener threw:", e);
      }
    });
  }

  /** React-style external store subscribe; useSyncExternalStore compatible. */
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = () => ({
    isConnected: this.isConnected,
    isAuthenticated: this.isAuthenticated,
    error: this.error,
    subscribedTopics: this.subscribedTopics,
  });

  registerRealtimeCallback(machineId: string, callback: RealtimeCallback) {
    this.realtimeCallbacks.set(machineId, callback);
  }

  unregisterRealtimeCallback(machineId: string) {
    this.realtimeCallbacks.delete(machineId);
  }

  private refreshSocketConfig() {
    this.serverUrl = getConfigValue("VITE_SOCKET_SERVER_URL");
    this.defaultTopic = getConfigValue("VITE_SOCKET_TOPIC");
    this.httpBase = this.serverUrl
      .replace(/^wss:\/\//, "https://")
      .replace(/^ws:\/\//, "http://");
    this.subscribedTopics = [];
  }

  private async authenticate(): Promise<boolean> {
    const user = getConfigValue("VITE_SOCKET_USERNAME");
    const pass = getConfigValue("VITE_SOCKET_PASSWORD");
    try {
      const response = await fetch(`${this.httpBase}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: user, password: pass }),
      });

      if (!response.ok) {
        const body = await response.text().catch(() => "");
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${body}`);
      }

      const data: AuthResponse = await response.json();
      this.authToken = data.token;
      this.isAuthenticated = true;
      this.error = null;
      this.notify();
      return true;
    } catch (err) {
      this.error =
        err instanceof Error ? err.message : t("socket.authUnknownError");
      this.isAuthenticated = false;
      this.notify();
      console.error("[WS] authenticate failed:", err);
      return false;
    }
  }

  private connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.authToken) {
        const msg = t("socket.tokenMissing");
        this.error = msg;
        this.notify();
        reject(new Error(msg));
        return;
      }

      try {
        this.socket = io(this.serverUrl, {
          auth: { token: this.authToken },
          transports: ["websocket"],
          upgrade: false,
        });

        this.socket.on("realtime_grv", (p) => {
          this.processRealtimeGrvData(p);
        });
        this.socket.on("data", (p) => {
          this.processRealtimeGrvData(p);
        });
        this.socket.on(this.defaultTopic, (p) => {
          try {
            this.processRealtimeGrvData(p);
          } catch (err) {
            console.error("[WS] Failed to process defaultTopic payload:", err);
          }
        });
        this.socket.on("message", (p) => {
          try {
            this.processRealtimeGrvData(p);
          } catch (err) {
            console.error("[WS] Failed to process 'message' payload:", err);
          }
        });

        this.socket.on("connect", () => {
          this.isConnected = true;
          this.error = null;
          this.subscribeToTopic();
          this.notify();
          resolve();
        });

        this.socket.on("disconnect", (reason) => {
          console.warn("[WS] disconnected:", reason);
          this.isConnected = false;
          this.notify();
        });

        this.socket.on("connect_error", (err) => {
          console.error("[WS] connect_error:", err.message);
          this.isConnected = false;
          this.error = err.message || "Erro de conexão";
          this.notify();
          reject(err);
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : t("socket.unknownError");
        this.error = msg;
        this.notify();
        reject(err);
      }
    });
  }

  subscribeToTopic() {
    if (!this.socket || !this.isConnected) {
      this.error = t("socket.notConnected");
      this.notify();
      return;
    }
    const topic = this.defaultTopic;
    try {
      this.socket.emit("subscribe", { topic }, () => {});
      if (!this.subscribedTopics.includes(topic)) {
        this.subscribedTopics = [...this.subscribedTopics, topic];
        this.notify();
      }
    } catch (err) {
      console.error("[WS] subscribe error:", err);
      this.error =
        err instanceof Error ? err.message : t("socket.subscribeError");
      this.notify();
    }
    // Re-assinar tópicos por máquina (importante após reconexão)
    this.flushMachineSubscriptions();
  }

  /**
   * Prefixo dos tópicos por máquina. No backend os dados de cada device são
   * publicados em `<host>-maxiot-<serial>` (ex: "hidalgo.grvmax.app.br-maxiot-TST00999").
   * O host é derivado de VITE_API_BASE_URL.
   */
  private machineTopicPrefix(): string {
    try {
      const base = getConfigValue("VITE_API_BASE_URL");
      return new URL(base).hostname;
    } catch {
      return "";
    }
  }

  private emitMachineSubscribe(serial: string) {
    if (!this.socket || !this.isConnected) return;
    const prefix = this.machineTopicPrefix();
    if (!prefix) {
      console.warn("[WS] ⚠️ prefixo de tópico de máquina vazio (VITE_API_BASE_URL inválido)");
      return;
    }
    const topic = `${prefix}-maxiot-${serial}`;
    this.socket.emit("subscribe", { topic }, () => {});
  }

  /** Re-emite o subscribe para todos os serials registrados. */
  private flushMachineSubscriptions() {
    for (const serial of this.machineSerials) {
      this.emitMachineSubscribe(serial);
    }
  }

  /** Registra serials e assina seus tópicos (agora se conectado, ou no próximo connect). */
  addMachineSubscriptions(serials: string[]) {
    for (const serial of serials) {
      if (!serial) continue;
      const had = this.machineSerials.has(serial);
      this.machineSerials.add(serial);
      if (!had && this.isConnected) this.emitMachineSubscribe(serial);
    }
  }

  /** Remove serials do conjunto monitorado (não re-assina no próximo connect). */
  removeMachineSubscriptions(serials: string[]) {
    for (const serial of serials) this.machineSerials.delete(serial);
  }

  async initialize(): Promise<void> {
    if (this.isInitializing) return;
    this.isInitializing = true;
    try {
      this.refreshSocketConfig();
      const ok = await this.authenticate();
      if (!ok) throw new Error(t("socket.authFailed"));
      await this.connect();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t("socket.initError");
      this.error = msg;
      this.notify();
      throw err;
    } finally {
      this.isInitializing = false;
    }
  }

  disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
      this.notify();
    }
  }

  private processRealtimeGrvData(data: any) {
    try {
      const payload =
        data?.payload ?? data?.metadata?.payload ?? data?.metadata ?? data ?? null;

      const docname =
        payload?.docname ?? data?.docname ?? data?.metadata?.docname;
      let serial =
        payload?.serial ??
        payload?.serial_device ??
        data?.serial ??
        data?.metadata?.serial ??
        null;
      let tag =
        payload?.default_tag ??
        payload?.tag ??
        data?.default_tag ??
        data?.tag ??
        null;

      if ((!serial || !tag) && typeof docname === "string" && docname.includes("-")) {
        const [docSerial, docTag] = docname.split("-");
        serial = serial ?? docSerial;
        tag = tag ?? docTag;
      }

      if (!serial || !tag) {
        console.warn("[WS] ⛔ descartado: serial ou tag ausente", { serial, tag });
        return;
      }

      const doctype = payload?.doctype ?? data?.doctype ?? "Device State";
      if (doctype !== "Device State") {
        console.warn(`[WS] ⛔ descartado: doctype="${doctype}" (esperado "Device State")`);
        return;
      }

      const value = payload?.value ?? data?.value ?? null;
      const timestamp =
        payload?.timestamp ?? data?.timestamp ?? new Date().toISOString();

      const callback = this.realtimeCallbacks.get(serial);
      if (callback) callback(tag, value, doctype);

      window.dispatchEvent(
        new CustomEvent("deviceStateUpdate", {
          detail: { serial, tag, value, doctype, timestamp },
        }),
      );

      const normalizeSerial = (v?: string | null) =>
        v ? decodeURIComponent(String(v).trim()).toUpperCase() : "";

      const currentPath = window.location.pathname;

      // O cache de status atualiza em QUALQUER tela (inclusive /analytics):
      // é ele que faz o dashboard/lista nascerem certos ao voltar. Os
      // CustomEvents adiante continuam com gate por página.
      if (tag === "DFT0001") {
        const statusValue = ensureStringValue(value);
        if (statusValue) machineStatusUpdater(serial, statusValue);
      }

      // /analytics shows historical charts that aren't invalidated by real-time pings.
      if (currentPath === "/analytics") return;

      const isOnMachineDetailsPage =
        currentPath.startsWith("/machines/") || currentPath.includes("/machine/");
      const currentMachineSerialRaw = isOnMachineDetailsPage
        ? currentPath.split("/").filter(Boolean).pop() || ""
        : "";
      const normalizedPayloadSerial = normalizeSerial(serial);
      const normalizedVisibleSerial = normalizeSerial(currentMachineSerialRaw);

      const isOnMachinesPage = currentPath === "/machines";
      const isOnDashboardPage = currentPath === "/" || currentPath === "/dashboard";
      const isOnAlarmsPage =
        currentPath === "/alarms" || currentPath.startsWith("/alarms");

      if (tag === "DFT0001") {
        const statusValue = ensureStringValue(value);
        if (!statusValue) return;

        if (
          isOnMachineDetailsPage &&
          normalizedPayloadSerial === normalizedVisibleSerial
        ) {
          window.dispatchEvent(
            new CustomEvent("statusUpdate", { detail: { serial, statusValue } }),
          );
        }

        if (isOnMachinesPage || isOnDashboardPage) {
          window.dispatchEvent(
            new CustomEvent("machineCardStatusUpdate", {
              detail: { serial, statusValue },
            }),
          );
        }
        return;
      }

      if (tag === "DFT0007") {
        const forceValue = ensureNumericValue(value);
        if (forceValue === null) return;
        if (
          !isOnMachineDetailsPage ||
          normalizedPayloadSerial !== normalizedVisibleSerial
        ) return;
        window.dispatchEvent(
          new CustomEvent("forceGaugeUpdate", { detail: { serial, forceValue } }),
        );
        return;
      }

      if (tag === "DFT0006") {
        const speedValue = ensureNumericValue(value);
        if (speedValue === null) return;
        if (
          !isOnMachineDetailsPage ||
          normalizedPayloadSerial !== normalizedVisibleSerial
        ) return;
        window.dispatchEvent(
          new CustomEvent("speedGaugeUpdate", { detail: { serial, speedValue } }),
        );
        return;
      }

      if (tag === "DFT0002") {
        const totalStrokesValue = ensureNumericValue(value);
        if (totalStrokesValue === null) return;
        if (
          !isOnMachineDetailsPage ||
          normalizedPayloadSerial !== normalizedVisibleSerial
        ) return;
        window.dispatchEvent(
          new CustomEvent("totalStrokesUpdate", {
            detail: { serial, totalStrokesValue },
          }),
        );
        return;
      }

      if (tag === "DFT0003") {
        const operationModeValue = ensureStringValue(value);
        if (!operationModeValue) return;
        if (
          !isOnMachineDetailsPage ||
          normalizedPayloadSerial !== normalizedVisibleSerial
        ) return;
        window.dispatchEvent(
          new CustomEvent("operationModeUpdate", {
            detail: { serial, operationModeValue },
          }),
        );
        return;
      }

      if (tag === "DFT0022") {
        if (
          isOnMachineDetailsPage &&
          normalizedPayloadSerial === normalizedVisibleSerial
        ) {
          window.dispatchEvent(
            new CustomEvent("alarmDistributionUpdate", {
              detail: { serial, value },
            }),
          );
        }
        if (isOnDashboardPage) {
          window.dispatchEvent(
            new CustomEvent("dashboardAlarmsUpdate", {
              detail: { serial, value },
            }),
          );
        }
        if (isOnAlarmsPage) {
          window.dispatchEvent(
            new CustomEvent("alarmsListUpdate", { detail: { serial, value } }),
          );
        }
        return;
      }

      if (tag === "DFT0023") {
        const angleValue = ensureNumericValue(value);
        if (angleValue === null) return;

        if (
          isOnMachineDetailsPage &&
          normalizedPayloadSerial === normalizedVisibleSerial
        ) {
          window.dispatchEvent(
            new CustomEvent("angleGaugeUpdate", { detail: { serial, angleValue } }),
          );
        }
        if (isOnMachinesPage) {
          window.dispatchEvent(
            new CustomEvent("machineCardAngleUpdate", {
              detail: { serial, angleValue },
            }),
          );
        }
        return;
      }
    } catch (err) {
      console.error("[MESSAGE] Erro ao processar dados:", err);
    }
  }
}

export const socketService = new SocketService();
